/**
 * Portal registry — per-portal feature/nav/home configuration.
 *
 * Keyed by the same `BrandKey` as the brand registry and selected by the same
 * build variable. The Peraso entry reproduces today's behaviour exactly (the
 * DefaultLayout nav order and the HomeView `shortcuts` groups), so wiring the
 * consuming surfaces to this layer in SD-2-02/03/04 is behaviour-preserving.
 *
 * Label/title/icon overrides are omitted where the catalog default already
 * matches today's value (resolution = override ?? catalog default).
 */
import type { BrandKey, IPortalConfig, FeatureKey } from './portal.types'

const PERASO_FEATURES: FeatureKey[] = [
  'home',
  'documents',
  'software',
  'hardware',
  'training',
  'link_budget',
  'knowledge_base',
  'legal',
]

const peraso: IPortalConfig = {
  key: 'peraso',
  // All features reachable. Legal is routed but intentionally absent from `nav`
  // (it is not a drawer item today).
  features: [...PERASO_FEATURES],
  // Current DefaultLayout `tabs` order: Knowledge Base precedes Link Budget,
  // and Legal is not present. Labels equal the catalog defaults, so no overrides.
  nav: [
    { feature: 'home' },
    { feature: 'documents' },
    { feature: 'software' },
    { feature: 'hardware' },
    { feature: 'training' },
    { feature: 'knowledge_base' },
    { feature: 'link_budget' },
  ],
  // Reproduces HomeView `shortcuts` verbatim: an external "Quick Links" group
  // and an internal "Getting Started" group (titles/subtitles/icons preserved).
  home: [
    {
      title: 'Quick Links',
      cards: [
        {
          title: 'Support Ticketing System',
          subtitle: 'Create a new support ticket or view existing ones.',
          icon: 'mdi-chat-processing-outline',
          href: 'https://perasoinc.atlassian.net/servicedesk/customer/user/login',
        },
        {
          title: 'Git Repository',
          subtitle: 'Access software source code and binary for our products',
          icon: 'mdi-github',
          href: 'https://git.portal.perasoinc.com/',
        },
        {
          title: 'Distributor Online Store',
          subtitle: 'Purchase all our 60GHz modules and evaluation kits online',
          icon: 'mdi-store-outline',
          href: 'https://shop.richardsonrfpd.com/Supplier/Index/Peraso',
        },
      ],
    },
    {
      title: 'Getting Started',
      cards: [
        {
          feature: 'documents',
          title: 'Product Documents',
          subtitle:
            'Learn more about our products and technology with detailed data sheets and app notes.',
          icon: 'mdi-notebook-outline',
        },
        {
          feature: 'knowledge_base',
          title: 'Knowledge Base',
          subtitle: 'Explore our comprehensive knowledge base of articles and guides.',
          icon: 'mdi-book-open-outline',
        },
        {
          feature: 'link_budget',
          title: 'Link Budget Calculator',
          subtitle: 'Calculate link budget parameters for our 60GHz wireless products',
          icon: 'mdi-calculator',
        },
        {
          feature: 'software',
          title: 'Legacy Software Packages',
          subtitle: 'Access our software deliverables for 60GHz products',
          icon: 'mdi-xml',
        },
        {
          feature: 'hardware',
          title: 'Reference Designs',
          subtitle:
            'Access hardware reference design kits including mechanical and thermal designs',
          icon: 'mdi-memory',
        },
        {
          feature: 'training',
          title: 'Training Materials',
          subtitle: 'Browse training materials, tutorials and guides for our 60GHz products',
          icon: 'mdi-school-outline',
        },
      ],
    },
  ],
  // No `pageTitles` overrides: the catalog's `defaultPageTitle`s ARE Peraso's
  // wording (including "Legacy Software Packages"). Restating them here would be
  // a redundant override that silently wins over — and could drift from — the
  // catalog and routes.ts.
}

// SD-2-05: Real DUNE configuration.
// Enabled features: home, documents, software, training, knowledge_base, legal.
// Excluded: hardware (Reference Designs) and link_budget (Link Budget Calculator).
// Legal is reachable but not in the nav drawer, same pattern as Peraso.
const dune: IPortalConfig = {
  key: 'dune',
  features: [
    'home',
    'documents',
    'software',
    'training',
    'knowledge_base',
    'legal',
  ],
  // Nav drawer: 5 items — Peraso's nav minus Hardware and Link Budget.
  // Labels equal the catalog defaults, so no overrides needed.
  nav: [
    { feature: 'home' },
    { feature: 'documents' },
    { feature: 'software' },
    { feature: 'training' },
    { feature: 'knowledge_base' },
  ],
  home: [
    {
      title: 'Quick Links',
      // DUNE has no Git repository or distributor store; instead it links out to
      // the standalone DUNE tools. The Atlassian URL is shared with the Peraso
      // build (PM confirmed).
      cards: [
        {
          title: 'Support Ticketing System',
          subtitle: 'Create a new support ticket or view existing ones.',
          icon: 'mdi-chat-processing-outline',
          href: 'https://perasoinc.atlassian.net/servicedesk/customer/user/login',
        },
        {
          title: 'Link Budget Calculator',
          subtitle: 'Estimate wireless link performance and plan deployments for your network.',
          icon: 'mdi-calculator-variant-outline',
          href: 'https://linkbudget.dunewireless.com/',
        },
        {
          title: 'Dune Alignment Tool',
          subtitle: 'Used as a theoretical alignment aim for positioning your radios.',
          icon: 'mdi-target',
          href: 'https://align-tool.dunewireless.com/',
        },
        {
          title: 'Network Planning Tool',
          subtitle: 'Design and plan your wireless network deployments.',
          icon: 'mdi-map-marker-path',
          href: 'https://network-planning.dunewireless.com/',
        },
      ],
    },
    {
      title: 'Getting Started',
      // The 4 enabled internal features. Software card uses "Software Packages"
      // (not "Legacy Software Packages") — the epic's rename example per SD-2-05.
      // Link Budget Calculator and Reference Designs are excluded (features off).
      cards: [
        {
          feature: 'documents',
          title: 'Product Documents',
          subtitle:
            'Learn more about our products and technology with detailed data sheets and app notes.',
          icon: 'mdi-notebook-outline',
        },
        {
          feature: 'knowledge_base',
          title: 'Knowledge Base',
          subtitle: 'Explore our comprehensive knowledge base of articles and guides.',
          icon: 'mdi-book-open-outline',
        },
        {
          feature: 'software',
          title: 'Software Packages',
          subtitle: 'Access our software deliverables for 60GHz products',
          icon: 'mdi-xml',
        },
        {
          feature: 'training',
          title: 'Training Materials',
          subtitle: 'Browse training materials, tutorials and guides for our 60GHz products',
          icon: 'mdi-school-outline',
        },
      ],
    },
  ],
  // DUNE drops the "Legacy" qualifier — the software page heading and document
  // title read "Software Packages", matching the home card above.
  pageTitles: {
    software: 'Software Packages',
  },
}

export const PORTALS: Record<BrandKey, IPortalConfig> = {
  peraso,
  dune,
}
